import * as React from 'react';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    TextField, Button, CircularProgress,
    Box, Tooltip
} from '@mui/material';
import axios from 'axios';
import { logout, getLocalToken } from '../utils';
import DeleteDialog from './DeleteDialog';
const api = axios.create({
    baseURL: '/admin/api/v1',
});

api.interceptors.request.use((config) => {
    const token = getLocalToken();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

export default function Events() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [searchPhrase, setSearchPhrase] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    const [sortModel, setSortModel] = useState([]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);

    const abortRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchPhrase);
            setPaginationModel(prev => ({
                ...prev,
                page: 0,
            }));
        }, 300);

        return () => clearTimeout(handler);
    }, [searchPhrase]);

    const fetchData = useCallback(async () => {
        const currentRequestId = ++requestIdRef.current;

        if (abortRef.current) {
            abortRef.current.abort();
        }

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoading(true);

            const { page, pageSize } = paginationModel;
            const offset = page * pageSize;

            let query = `?offset=${offset}&limit=${pageSize}`;

            if (debouncedSearch) {
                query += `&search=${encodeURIComponent(debouncedSearch)}`;
            }

            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                query += `&sort_by=${field}&sort_order=${sort}`;
            }

            const res = await api.get(`/events${query}`, {
                signal: controller.signal,
            });

            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            const payload = res?.data?.payload || {};

            setRows(payload.items || []);
            setTotalRows(payload.total || 0);

        } catch (err) {
            if (err.name === 'CanceledError') return;

            console.error(err);

            if (err.response?.status === 401) {
                logout();
            }
        } finally {
            if (requestIdRef.current === currentRequestId) {
                setLoading(false);
            }
        }
    }, [paginationModel, debouncedSearch, sortModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            field: 'uid',
            headerName: 'id',
            width: 85,
            renderCell: (params) => {
                const imageUrl = params.row?.media?.[0]?.url;
                return (
                    <Tooltip
                        slotProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: 'transparent',
                                    p: 0,
                                    boxShadow: 'none',
                                    maxWidth: 'none',
                                },
                            },
                            arrow: {sx: {color: '#fff'}},
                        }}
                        title={
                            imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt="preview"
                                    style={{
                                        width: 280,
                                        maxHeight: 350,
                                        objectFit: 'contain',
                                        display: 'block',
                                        borderRadius: 12,
                                        background: '#fff',
                                        padding: 4,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    }}
                                />
                            ) : ('No image')
                        }
                    >
            <span
                style={{
                    cursor: 'pointer',
                    textDecoration: 'underline dotted',
                }}
            >
                {params.value}
            </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'author',
            headerName: 'Author',
            width: 180,
            renderCell: (params) => {
                const { first_name, last_name, email } = params.row;
                const fullName = `${first_name ? first_name : ''} ${last_name ? last_name : ''}`.trim();
                if (fullName || false) {
                    return (
                        <a
                            href={`/users?search=${(email)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#1976d2",
                                textDecoration: "none",
                                fontWeight: 500,
                            }}
                            title="Open Users section"
                        >
                            <span>{fullName}</span>
                        </a>
                    );
                }
                return fullName;
            },
        },
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            minWidth: 160,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                return value.length > 80 ? `${value.substring(0, 80)}...` : value;
            },
        },
        {
            field: 'description',
            flex: 1,
            headerName: 'Description',
            minWidth: 450,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                return value.length > 100 ? `${value.substring(0, 100)}...` : value;
            },
        },
        {
            field: 'category',
            headerName: 'Category',
            width: 130,
        },
        {
            field: 'starts_at',
            headerName: 'Start At',
            width: 170,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                return new Date(value).toLocaleString(undefined, {
                    hour12: false,
                });
            },
        },
        {
            field: 'ends_at',
            headerName: 'End At',
            width: 170,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                return new Date(value).toLocaleString(undefined, {
                    hour12: false,
                });
            },
        },
        {
            field: 'price',
            headerName: 'Price',
            width: 80,
            valueGetter: (value) => {
                if (
                    value === null ||
                    value === undefined ||
                    value === 0
                ) {
                    return 'N/A';
                }

                return `${value}`;
            },
        },
        {
            field: 'location',
            headerName: 'Location',
            width: 100,
            renderCell: (params) => {
                const { location, lat, long } = params.row;
                if (lat && long) {
                    return (
                        <a
                            href={`https://maps.google.com/?q=${lat},${long}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#1976d2",
                                textDecoration: "none",
                                fontWeight: 500,
                            }}
                            title={`Open ${location} in Google Maps`}
                        >

                            <span>{location}</span>
                        </a>
                    );
                }
                return location;
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={
                        deleteLoading === params.row.uid ? null : <DeleteIcon />
                    }
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(params.row);
                    }}
                    disabled={deleteLoading === params.row.uid}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    }}
                >
                    {deleteLoading === params.row.uid
                        ? <CircularProgress size={16} />
                        : 'Delete'}
                </Button>
            ),
        },
    ], [deleteLoading]);

    const handleDeleteClick = (event) => {
        setSelectedEvent(event);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedEvent) return;

        try {
            setDeleteLoading(selectedEvent.uid);

            await api.delete(`/events/${selectedEvent.uid}`);

            setRows((prev) =>
                prev.filter((row) => row.uid !== selectedEvent.uid)
            );

            setTotalRows((prev) => prev - 1);

            setDeleteDialogOpen(false);
            setSelectedEvent(null);
        } catch (err) {
            console.error(err);
        } finally {
            setDeleteLoading(null);
        }
    };

    return (
        <>
            <Box
                mb={1}
                display="flex"
                alignItems="flex-end"
            >
                <TextField
                    label="Search"
                    variant="standard"
                    size="small"
                    value={searchPhrase}
                    onChange={(e) => setSearchPhrase(e.target.value)}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            <br />

            <DataGrid
                disableColumnMenu
                rows={rows}
                onRowClick={(params) => {
                    window.open(`/events/${params.row.uid}`, '_blank');
                }}
                getRowId={(row) => row.uid}
                columns={columns}
                loading={loading}
                rowCount={totalRows}
                paginationMode="server"
                sortingMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                disableAutosize
                onSortModelChange={(model) => {
                    setSortModel(model);
                    setPaginationModel(prev => ({
                        ...prev,
                        page: 0,
                    }));
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                    border: 0,
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                        {
                            outline: 'none',
                        },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                        {
                            outline: 'none',
                        },
                    '& .MuiDataGrid-cell:focus-visible': {
                        outline: 'none',
                    },
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Box p={2}>No events found</Box>
                    ),
                }}
            />
            <DeleteDialog
                open={deleteDialogOpen}
                loading={deleteLoading !== null}
                title="Confirm delete action"
                message={<span> Are you sure you want to <b>delete</b> event with title: <b>"{selectedEvent?.title}"?</b> </span>}
                onClose={() => {
                    if (deleteLoading) return;
                    setDeleteDialogOpen(false);
                    setSelectedEvent(null);
                }}
                onConfirm={handleDeleteConfirm}
            />

        </>
    );
}